import { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { useFetch, Field } from "@/lib/ui";
import { useLang } from "@/context/LangContext";
import { Empty } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save } from "lucide-react";

const TERMS = ["Term 1", "Term 2", "Term 3", "Term 4"];
// General: Quiz 10 + Projet&Devoirs 20 + Participation 10 + Lecture 10 + Examen 50
// Français: Lecture 20 + Oral 10 + Écrit 10 + Quiz 10 + Examen 50
const SCHEMES = {
  general: [
    { k: "quizzes", en: "Quiz 10%", fr: "Quiz 10%" },
    { k: "projects", en: "Project & HW 20%", fr: "Projet & devoirs 20%" },
    { k: "participation", en: "Participation 10%", fr: "Participation 10%" },
    { k: "reading", en: "Reading 10%", fr: "Lecture 10%" },
    { k: "exam", en: "Exam 50%", fr: "Examen 50%" },
  ],
  french: [
    { k: "reading", en: "Reading 20%", fr: "Lecture 20%" },
    { k: "oral", en: "Speaking/Listening 10%", fr: "Oral 10%" },
    { k: "writing", en: "Writing 10%", fr: "Écrit 10%" },
    { k: "quizzes", en: "Quiz 10%", fr: "Quiz 10%" },
    { k: "exam", en: "Exam 50%", fr: "Examen 50%" },
  ],
};
const finalOf = (r, t) => {
  const n = (k) => +r[k] || 0;
  const v = t === "french"
    ? n("reading") * 0.2 + n("oral") * 0.1 + n("writing") * 0.1 + n("quizzes") * 0.1 + n("exam") * 0.5
    : n("quizzes") * 0.1 + n("projects") * 0.2 + n("participation") * 0.1 + n("reading") * 0.1 + n("exam") * 0.5;
  return Math.round(v * 100) / 100;
};
const EMPTY = { projects: "", quizzes: "", participation: "", reading: "", oral: "", writing: "", exam: "", books_read: "" };

export default function TermGrades() {
  const { L } = useLang();
  const { data: classes } = useFetch(() => api.get("/classes"));
  const { data: teaching } = useFetch(() => api.get("/me/teaching"));
  const [classId, setClassId] = useState("");
  const [term, setTerm] = useState("Term 1");
  const [subjectType, setSubjectType] = useState("general");
  const [students, setStudents] = useState([]);
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(false);
  const scoped = !!teaching?.scoped;
  const allowedTypes = scoped ? (teaching?.types || []) : ["general", "french"];
  useEffect(() => { if (allowedTypes.length && !allowedTypes.includes(subjectType)) setSubjectType(allowedTypes[0]); }, [teaching]); // eslint-disable-line

  useEffect(() => {
    if (!classId) return;
    setLoading(true);
    (async () => {
      try {
        const all = await api.get("/students");
        const inClass = all.filter((s) => s.class_id === classId && s.status === "accepted");
        setStudents(inClass);
        const next = {};
        for (const s of inClass) {
          const tg = (await api.get(`/students/${s.id}/term-grades`)).find((x) => x.term === term);
          next[s.id] = tg ? { ...EMPTY, ...tg } : { ...EMPTY };
        }
        setRows(next);
      } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setLoading(false); }
    })();
  }, [classId, term]);

  const setV = (sid, k, v) => setRows((p) => ({ ...p, [sid]: { ...p[sid], [k]: v } }));
  const save = async (sid) => {
    const r = rows[sid] || {};
    try {
      await api.raw.put(`/students/${sid}/term-grade`, {
        term, subject_type: subjectType,
        projects: +r.projects || 0, quizzes: +r.quizzes || 0, participation: +r.participation || 0,
        reading: +r.reading || 0, oral: +r.oral || 0, writing: +r.writing || 0,
        exam: +r.exam || 0, books_read: +r.books_read || 0,
      });
      toast.success(L("Saved", "Enregistré"));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const cols = SCHEMES[subjectType];

  return (
    <div className="animate-fade-up">
      <div className="mb-4">
        <h2 className="font-heading font-extrabold text-2xl">{L("Term Final Grade", "Note finale par terme")}</h2>
        <p className="text-muted-foreground text-sm">{subjectType === "french"
          ? L("French: Reading 20% + Speaking 10% + Writing 10% + Quiz 10% + Exam 50%.", "Français : Lecture 20% + Oral 10% + Écrit 10% + Quiz 10% + Examen 50%.")
          : L("General: Quiz 10% + Project & HW 20% + Participation 10% + Reading 10% + Exam 50%.", "Général : Quiz 10% + Projet & devoirs 20% + Participation 10% + Lecture 10% + Examen 50%.")} {L("This becomes the official term grade. Scores /100.", "Devient la note officielle du terme. Notes sur 100.")}</p>
      </div>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="w-64"><Field label={L("Class", "Classe")}>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger data-testid="tg-class-select"><SelectValue placeholder={L("Select class", "Choisir la classe")} /></SelectTrigger>
            <SelectContent>{(classes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.grade_level}</SelectItem>)}</SelectContent>
          </Select>
        </Field></div>
        <div className="w-40"><Field label={L("Term", "Trimestre")}>
          <Select value={term} onValueChange={setTerm}>
            <SelectTrigger data-testid="tg-term-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field></div>
        <div className="w-52"><Field label={L("Subject type", "Type de matière")}>
          <Select value={subjectType} onValueChange={setSubjectType} disabled={scoped && allowedTypes.length <= 1}>
            <SelectTrigger data-testid="tg-subject-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {allowedTypes.includes("general") && <SelectItem value="general">{L("General subject", "Matière générale")}</SelectItem>}
              {allowedTypes.includes("french") && <SelectItem value="french">{L("French", "Français")}</SelectItem>}
            </SelectContent>
          </Select>
        </Field></div>
        {scoped && <p className="w-full text-xs text-muted-foreground" data-testid="tg-teaching-note">{allowedTypes.length === 0 ? L("You are not assigned to any graded subject in the timetable.", "Vous n'êtes assigné à aucune matière notée dans l'emploi du temps.") : L(`You can grade: ${(teaching?.subjects || []).join(", ") || (allowedTypes.includes("french") ? "Français" : "matières générales")}. Other subjects are locked.`, `Vous pouvez noter : ${(teaching?.subjects || []).join(", ") || (allowedTypes.includes("french") ? "Français" : "matières générales")}. Les autres matières sont verrouillées.`)}</p>}
      </div>

      {scoped && allowedTypes.length === 0 ? <Empty title={L("No graded subject assigned to you", "Aucune matière notée ne vous est assignée")} /> :
        !classId ? <Empty title={L("Select a class to begin", "Choisissez une classe")} /> :
        loading ? <Empty title="…" /> :
        students.length === 0 ? <Empty title={L("No students in this class", "Aucun élève dans cette classe")} /> : (
          <div className="border border-border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{L("Student", "Élève")}</TableHead>
                {cols.map((c) => <TableHead key={c.k}>{L(c.en, c.fr)}</TableHead>)}
                <TableHead>{L("Books read", "Livres lus")}</TableHead>
                <TableHead>{L("Final /20", "Note /20")}</TableHead>
                <TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>{students.map((s) => {
                const r = rows[s.id] || {};
                const fin = finalOf(r, subjectType);
                return (
                  <TableRow key={s.id} data-testid={`tg-row-${s.id}`}>
                    <TableCell className="font-medium">{s.first_name} {s.last_name}</TableCell>
                    {cols.map((c) => (
                      <TableCell key={c.k}><Input type="number" min="0" max="100" className="w-20 h-8" value={r[c.k] ?? ""} onChange={(e) => setV(s.id, c.k, e.target.value)} data-testid={`tg-${c.k}-${s.id}`} /></TableCell>
                    ))}
                    <TableCell><Input type="number" min="0" className="w-20 h-8" value={r.books_read ?? ""} onChange={(e) => setV(s.id, "books_read", e.target.value)} data-testid={`tg-books_read-${s.id}`} /></TableCell>
                    <TableCell><span className="font-heading font-bold text-primary" data-testid={`tg-final-${s.id}`}>{(fin / 5).toFixed(2)}</span> <span className="text-xs text-muted-foreground">({fin})</span></TableCell>
                    <TableCell><Button size="sm" onClick={() => save(s.id)} data-testid={`tg-save-${s.id}`}><Save className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                );
              })}</TableBody>
            </Table>
          </div>
        )}
    </div>
  );
}
